/**
 * QuarterlyUpdateChart Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * An interactive doughnut chart component that displays quarterly update data with integrated
 * PDF viewing capabilities. Combines chart visualization with document access through modal
 * interfaces, supporting multiple simultaneous PDF views.
 * 
 * Features:
 * - Interactive doughnut chart using Chart.js
 * - Click-to-open PDF functionality for chart segments
 * - Multiple PDF modal management with z-index stacking
 * - Automatic data sorting by quarter and year
 * - Custom chart labeling and tooltips
 * - Hover effects and cursor management
 * - Responsive layout with image header and description
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.imageUrl - URL of the header image to display
 * @param {string} props.title - Title text for the chart section
 * @param {string} props.description - Description text below the title
 * @param {Object} props.chartData - Chart.js data object for the doughnut chart
 * @param {Array} props.pdfData - Array of PDF objects linked to chart segments
 * @param {Object} props.chartStyle - CSS style object for chart container
 * @returns {JSX.Element} The rendered quarterly update chart component
 * 
 * @example
 * ```jsx
 * <QuarterlyUpdateChart 
 *   imageUrl="/images/quarterly-header.png"
 *   title="Quarterly Updates Overview"
 *   description="Interactive chart showing quarterly publications"
 *   chartData={chartDataObject}
 *   pdfData={pdfDataArray}
 *   chartStyle={{ width: '400px', height: '400px' }}
 * />
 * ```
 */

import React, { useState, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import PDFModal from '../../modals/PDFModal';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

function QuarterlyUpdateChart({ imageUrl, title, description, chartData, pdfData, chartStyle }) {

    /**
     * Filter PDF data to only include items with populated Quarter field
     * and generate corresponding chart data dynamically
     */
    const { filteredPdfData, dynamicChartData } = useMemo(() => {
        if (!pdfData || !Array.isArray(pdfData)) {
            return {
                filteredPdfData: [],
                dynamicChartData: chartData || { labels: [], datasets: [] }
            };
        }

        // Filter PDF data to only include items with populated Quarter field
        const filtered = pdfData.filter(item => {
            const hasQuarter = item.Quarter && item.Quarter.trim() !== '';
            const hasYear = item.Year;
            const hasDoc = item.doc;
            
            return hasQuarter && hasYear && hasDoc;
        });

        // Fallback to original chartData if no filtered data
        if (filtered.length === 0) {
            return {
                filteredPdfData: pdfData,
                dynamicChartData: chartData
            };
        }

        // Generate chart data from filtered PDF data
        const labels = filtered.map(item => `${item.Quarter} ${item.Year}`);

        // Create chart data structure
        const generatedChartData = {
            labels: labels,
            datasets: chartData?.datasets ? chartData.datasets.map(dataset => ({
                ...dataset,
                data: labels.map((_, index) => dataset.data?.[index] || 1) // Use existing data or default to 1
            })) : [{
                data: labels.map(() => 1), // Default data if no datasets provided
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB', 
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#FF6384',
                    '#C9CBCF'
                ].slice(0, labels.length)
            }]
        };

        return {
            filteredPdfData: filtered,
            dynamicChartData: generatedChartData
        };
    }, [pdfData, chartData]);

    /**
     * Sort chart data by Quarter and Year chronologically
     * 
     * Creates a sorted version of the chart data where labels and datasets are reordered
     * to display quarters in chronological order (earliest first). This ensures consistent
     * visual presentation regardless of input data order.
     * 
     * @returns {Object|null} Sorted chart data object or null if no data provided
     */
    const sortedChartData = useMemo(() => {
        if (!dynamicChartData) return null;
        
        // Create a copy of the data to avoid mutating props
        const sortedData = {
            ...dynamicChartData,
            labels: [...dynamicChartData.labels],
            datasets: dynamicChartData.datasets.map(dataset => ({
                ...dataset,
                data: [...dataset.data]
            }))
        };

        // Create an array of indices to sort
        const indices = sortedData.labels.map((label, index) => {
            const [quarter, year] = label.split(' ');
            return { index, quarter: parseInt(quarter.replace('Q', '')), year: parseInt(year) };
        });

        // Sort indices based on year and quarter - REVERSE CHRONOLOGICAL
        // Start with current year's highest quarter and drill backwards
        indices.sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year; // Year DESC (2025, 2024, 2023...)
            return b.quarter - a.quarter; // Quarter DESC within year (Q4, Q3, Q2, Q1)
        });

        // Reorder the data based on sorted indices
        sortedData.labels = indices.map(i => dynamicChartData.labels[i.index]);
        sortedData.datasets = sortedData.datasets.map(dataset => ({
            ...dataset,
            data: indices.map(i => dataset.data[i.index])
        }));

        return sortedData;
    }, [dynamicChartData]);

    const [modals, setModals] = useState([]); // Manage multiple modals
    
    /**
     * Handle chart click events and open corresponding PDF modal
     * 
     * Processes click events on chart segments to identify the clicked quarter
     * and opens the associated PDF document in a modal. Uses the chart data
     * to map clicked segments to specific PDF documents.
     * 
     * @param {Event} event - Chart click event object
     * @param {Array} elements - Array of chart elements at click position
     * 
     * @example
     * ```javascript
     * // Triggered when user clicks on a chart segment
     * onChartClick(clickEvent, [chartElement])
     * ```
     */
    const onChartClick = (event, elements) => {
        if (elements.length > 0) {
            const clickedLabelIndex = elements[0].index;
            const selectedLabel = sortedChartData.labels[clickedLabelIndex];
            
            const selectedPDF = filteredPdfData.find((item) => `${item.Quarter} ${item.Year}` === selectedLabel);
            
            if (!selectedPDF) {
                alert(`No PDF document available for ${selectedLabel}`);
                return;
            }
            
            if (!selectedPDF.doc) {
                alert(`PDF document for ${selectedLabel} is not available`);
                return;
            }
            
            const fullTitle = `${title} - ${selectedLabel}`;
            openPDFModal(selectedPDF.id, fullTitle, selectedPDF.doc);
        }
    };

    /**
     * Open a PDF modal or bring existing modal to front
     * 
     * Manages multiple PDF modals with proper z-index stacking and positioning.
     * If a modal for the same PDF already exists, it brings it to the front.
     * Otherwise, creates a new modal with appropriate stacking order.
     * 
     * @param {string|number} id - Unique identifier for the PDF/modal
     * @param {string} title - Title to display in the modal header
     * @param {string} pdfUrl - URL of the PDF document to display
     * 
     * @example
     * ```javascript
     * openPDFModal('q1-2024', 'Q1 2024 Report', 'q1-2024.pdf')
     * ```
     */
    const openPDFModal = (id, title, pdfUrl) => {
        setModals((prevModals) => {
            const existingModalIndex = prevModals.findIndex((modal) => modal.id === id);
            if (existingModalIndex !== -1) {
                // If modal is already open, bring it to the front by updating zIndex
                const updatedModals = [...prevModals];
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex)) + 1;
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                // Open a new modal with calculated positioning and size
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex), 0) + 1;
                // Add new modal to state
                const newModal = {
                    id,
                    title,
                    pdfUrl,
                    isOpen: true, // Ensure the modal is open
                    zIndex: maxZIndex, // Set zIndex to be the highest
                };

                return [...prevModals, newModal];
            }
        });
    };

    /**
     * Close a PDF modal by ID
     * 
     * Marks the specified modal as closed while maintaining it in the modals array
     * for potential reopening. The modal component handles the actual visibility.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * closePDFModal('q1-2024')
     * ```
     */
    const closePDFModal = (id) => {
        setModals((prevModals) =>
            prevModals.map((modal) =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
    };

    /**
     * Doughnut chart configuration options
     * 
     * Configures Chart.js options for the doughnut chart including:
     * - Plugin settings for legend, data labels, and tooltips
     * - Event handlers for clicks and hover states
     * - Custom styling and formatting
     * 
     * @type {Object} Chart.js configuration object
     */
    const chartOptions = {
        plugins: {
            legend: { display: false },
            datalabels: {
                color: 'white',
                font: { weight: 'bold', size: 16 },
                formatter: (value, context) => context.chart.data.labels[context.dataIndex],
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => tooltipItem.label,
                    afterLabel: () => '',
                    title: () => '',
                },
            },
        },
        onClick: onChartClick,
        onHover: (event, chartElement) => {
            event.native.target.style.cursor = chartElement.length ? 'pointer' : 'default';
        },
    };

    // Safety check for chart data
    const chartToRender = sortedChartData || dynamicChartData || chartData;

    return (
        <>
            <img src={imageUrl} alt={title} className="widget-image" />
            <h2 className='main-header'>{title}</h2>
            <div className='chart-description summary'>{description}</div>
            <div className="chart-container" style={chartStyle}>
                {chartToRender && chartToRender.labels && chartToRender.labels.length > 0 ? (
                    <Doughnut data={chartToRender} options={chartOptions} />
                ) : (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '300px',
                        color: '#666',
                        fontSize: '1.1rem'
                    }}>
                        📊 Loading chart data...
                    </div>
                )}
            </div>

            {/* Render multiple PDFModals */}
            {modals.map((modal) => (
                <PDFModal
                    key={modal.id}
                    title={modal.title}
                    url={modal.pdfUrl}
                    show={modal.isOpen} // Conditionally render based on isOpen
                    onClose={(e) => {
                        e.stopPropagation(); // Prevent propagation on close event
                        closePDFModal(modal.id);
                    }} // Close modal on trigger
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                    style={{ zIndex: modal.zIndex }} // Apply zIndex
                    onBringToFront={() => openPDFModal(modal.id, modal.title, modal.pdfUrl)} // Bring modal to front
                    listItemId={modal.id}
                />
            ))}
        </>
    );
}

export default QuarterlyUpdateChart;
